import Base from '@jgile/laravel-vue/resources/js/models/Base';
import { set } from '@jgile/laravel-vue/resources/js/util';
import Collection from '@jgile/laravel-vue/resources/js/models/Collection';

const RESERVED = _.invert(['_uid', '_attributes', '_listeners', '_reference', '_attributes', '_mutations']);

export default class BaseModel extends Base {
    /**
     * @returns {Object} This model's saved, reference data.
     */
    get $() {
        return this._reference;
    }

    /**
     * @returns {Object} The collection that this model is registered to.
     */
    get collections() {
        return _.values(this._collections);
    }

    /**
     * Create collection of items.
     *
     * @param collection
     * @returns {Collection}
     */
    static collect(collection) {
        return _.map(collection, (item) => new this(item));
        // return new Collection(collection, { model: this });
    }

    /**
     * Construct model.
     *
     * @param attributes
     * @param collection
     * @param options
     * @returns {{}}
     */
    constructor(attributes = {}, collection = null, options = {}) {
        super(attributes, collection, options);

        // If is already an instance, return the instance.
        if (attributes && Object.getPrototypeOf(attributes).constructor === this.constructor) {
            return attributes;
        }

        Vue.set(this, '_attributes', {});
        Vue.set(this, '_mutations', {});
        Vue.set(this, '_reference', {});
        Vue.set(this, '_relationships', {});
        Vue.set(this, '_collections', {});

        // Register the given collection (if any) to the model. This is so that
        // the model can be added to the collection automatically when it is
        // created on save, or removed on delete.
        if (collection) {
            this.registerCollection(collection);
        }

        // this.registerRelationships();
        this.compileMutators();
        this.assignAttributes(attributes);
        this.boot();

        // if (this.isExisting()) {
        //     Bus.$on(`modelEvent/${this.$resource}/${this.identifier()}/push`, this.setAttribute);
        // }
    }

    static instance() {
        return new this();
    }

    static query(query) {
        let self = this.instance();
        self.getBuilder().set(query);

        return self;
    }

    static baseUrl(baseUrl) {
        let self = this.instance();
        self.baseUrl(baseUrl);

        return self;
    }

    static presenter(args) {
        let self = this.instance();
        self.presenter(args);

        return self;
    }

    static include(...args) {
        let self = this.instance();
        self.include(...args);

        return self;
    }

    static append(...args) {
        let self = this.instance();
        self.append(...args);

        return self;
    }

    static select(...fields) {
        let self = this.instance();
        self.select(...fields);

        return self;
    }

    static where(field, value) {
        let self = this.instance();
        self.where(field, value);

        return self;
    }

    static whereIn(field, array) {
        let self = this.instance();
        self.whereIn(field, array);

        return self;
    }

    static orderBy(...args) {
        let self = this.instance();
        self.orderBy(...args);

        return self;
    }

    static page(value) {
        let self = this.instance();
        self.page(value);

        return self;
    }

    static limit(value) {
        let self = this.instance();
        self.limit(value);

        return self;
    }

    static custom(...args) {
        let self = this.instance();
        self.custom(...args);

        return self;
    }

    static params(payload) {
        let self = this.instance();
        self.params(payload);

        return self;
    }

    static first() {
        let self = this.instance();

        return self.first();
    }

    static find(id) {
        let self = this.instance();

        return self.find(id);
    }

    static get(params = null) {
        let self = this.instance();

        return self.get(params);
    }

    /**
     * Default Attributes values.
     *
     * @returns {{}}
     */
    defaultAttributes() {
        return {};
    }

    /**
     * Model mutations.
     *
     * @returns {{}}
     */
    mutations() {
        return {};
    }

    /**
     * @returns {*} The value of an attribute after applying its mutations.
     */
    mutated(attribute, value) {
        let mutator = _.get(this._mutations, attribute);

        if (mutator) {
            return mutator(value);
        }

        return value;
    }

    /**
     * Compile mutations.
     */
    compileMutators() {
        this._mutations = _.mapValues(this.mutations(), (m) => _.flow(m));
    }

    /**
     * Registers an attribute on this model so that it can be accessed directly
     * on the model, passing through `get` and `set`.
     */
    registerAttribute(attribute) {
        // Protect against unwillingly using an attribute name that already
        // exists as an internal property or method name.
        if (_.has(RESERVED, attribute)) {
            throw new Error(`Can't use reserved attribute name '${attribute}'`);
        }

        // Create dynamic accessors and mutations so that we can update the
        // model directly while also keeping the model attributes in sync.
        Object.defineProperty(this, attribute, {
            get: () => this.getAttribute(attribute),
            set: (value) => this.setAttribute(attribute, value),
        });
    }

    // /**
    //  * Registre relationships;
    //  */
    // registerRelationships() {
    //     _.each(this.relationships(), (relationship, attribute) => {
    //         Object.defineProperty(this, attribute, {
    //             get: () => this.getRelationship(attribute),
    //             set: (value) => this.setAttribute(attribute, value),
    //         });
    //     });
    // }

    /**
     * Relationship getter method.
     *
     * @param attribute
     * @returns {Collection|*}
     */
    getRelationship(attribute) {
        if (!this._relationships[attribute]) {
            let relationship = this.relationships()[attribute];

            if (relationship.collection) {
                set(this._relationships, attribute, new Collection(this.getAttribute(attribute, {}), relationship));
            } else {
                set(this._relationships, attribute, new relationship.model(value));
            }
        }

        return this._relationships[attribute];
    }

    /**
     * Assigns all given model data to the model's attributes and reference.
     * This will also fill any gaps using the model's default attributes.
     *
     * @param {Object} attributes
     *
     * @returns {Object} The attributes that were assigned to the model.
     */
    assignAttributes(attributes) {
        this.setAttribute(_.defaults({}, attributes, _.cloneDeep(this.defaultAttributes())));
        this.syncAttributes();
    }

    /**
     * Sets the value of an attribute and registers the magic "getter" in a way
     * that is compatible with Vue's reactivity. This method should always be
     * used when setting the value of an attribute.
     *
     * @param  {string|Object}  attribute
     * @param  {*}              value
     *
     * @returns {*} The value that was set.
     */
    setAttribute(attribute, value) {
        // Allow batch set of multiple attributes at once, ie. set({...});
        if (_.isPlainObject(attribute)) {
            _.each(attribute, (value, key) => {
                this.setAttribute(key, value);
            });

            return;
        }

        let defined = this.hasAttribute(attribute);

        // Only register the pass-through property if it's not already set up.
        // If it already exists on the instance, we know it has been.
        if (!defined) {
            this.registerAttribute(attribute);
        }

        // Current value of the attribute, or `undefined` if not set
        let previous = this.getAttribute(attribute);

        // Mutate the value.
        value = this.mutated(attribute, value);

        set(this._attributes, attribute, value);

        // Only consider a change if the attribute was already defined.
        let changed = defined && !_.isEqual(previous, value);

        if (changed) {
            // Emit the change event after
            this.emit('change', { attribute, previous, value });
        }

        return value;
    }

    /**
     * Reverts all attributes back to their defaults, or `undefined` if a
     * default value is not defined.
     *
     * You can also pass one or an array of attributes to unsetAttribute.
     *
     * @param {string|string[]} attribute
     */
    unsetAttribute(attribute) {
        // We're cloning deep to avoid multiple references to the same object,
        // otherwise updating the attributes will also update the reference.
        let defaults = _.cloneDeep(this.defaults());

        // Unset either specific attributes or all attributes if none provided.
        let attributes = _.defaultTo(attribute, _.keys(this._attributes));

        // Unset either specific attributes or all attributes if none provided.
        _.each(_.castArray(attributes), (attribute) => {
            if (this.hasAttribute(attribute)) {
                set(this._attributes, attribute, _.get(defaults, attribute));
            }
        });
    }

    /**
     * Similar to `saved`, returns an attribute's value or a fallback value
     * if this model doesn't have the attribute.
     *
     * @param {string} attribute
     * @param {*}      fallback
     *
     * @returns {*} The value of the attribute or `fallback` if not found.
     */
    getAttribute(attribute, fallback) {
        return _.get(this._attributes, attribute, fallback);
    }

    /**
     * Similar to `get`, but accesses the saved attributes instead.
     *
     * This is useful in cases where you want to display an attribute but also
     * change it. For example, a modal with a title based on a model field, but
     * you're also editing that field. The title will be updating reactively if
     * it's bound to the active attribute, so bind to the saved one instead.
     *
     * @param {string} attribute
     *
     * @returns {*} The value of the attribute or `fallback` if not found.
     */
    savedAttribute(attribute) {
        return _.get(this._reference, attribute);
    }

    /**
     * Pick only selected attributes
     *
     * @param {string} pick
     *
     * @returns {*} The chosen attributes as array.
     */
    onlyAttributes(pick) {
        return _.pick(this._attributes, pick);
    }

    /**
     * Similar to `get`, but accesses the saved attributes instead.
     *
     * This is useful in cases where you want to display an attribute but also
     * change it. For example, a modal with a title based on a model field, but
     * you're also editing that field. The title will be updating reactively if
     * it's bound to the active attribute, so bind to the saved one instead.
     *
     * @param {string} attribute
     * @param {*}      fallback
     *
     * @returns {*} The value of the attribute or `fallback` if not found.
     */
    savedAttributes(attribute, fallback) {
        return _.get(this._reference, attribute, fallback);
    }

    /**
     * Determines if the model has an attribute.
     *
     * @param  {string}  attribute
     * @returns {boolean} `true` if an attribute exists, `false` otherwise.
     *                   Will return true if the object exists but is undefined.
     */
    hasAttribute(attribute) {
        return _.has(this._attributes, attribute);
    }

    /**
     * Returns an array of attribute names that have changed, or `false` if no
     * changes have been made since the last time this model was synced.
     *
     * @returns {Array|boolean} An array of changed attribute names, or `false`
     *                         if no attributes have changed since the last sync.
     */
    changedAttributes() {
        let changed = [];

        _.each(this._attributes, (value, attribute) => {
            if (!_.isEqual(value, this.savedAttributes(attribute))) {
                changed.push(attribute);
            }
        });

        return !_.isEmpty(changed) ? changed : false;
    }

    /**
     * Sync the current attributes to the reference attributes. This is usually
     * only called on save. We have to clone the values otherwise we
     * end up with references to the same object in both attribute sets.
     *
     * You can also pass one or an array of attributes to sync.
     *
     * @param {string|string[]} attribute
     */
    syncAttributes(attribute) {
        // We're cloning deep to avoid multiple references to the same object,
        // otherwise updating the attributes will also update the reference.
        // Set each saved attribute to its active equivalent.
        let active = _.cloneDeep(this._attributes);

        // Sync either specific attributes or all attributes if none provided.
        if (_.isUndefined(attribute)) {
            set(this, '_reference', active);
        } else {
            _.each(_.castArray(attribute), (attribute) => {
                set(this._reference, attribute, _.get(active, attribute));
            });
        }

        this.emit('sync');
    }

    /**
     * Model relationships.
     *
     * @returns {{}}
     */
    relationships() {
        return {};
    }

    /**
     * Refetch the current model.
     * Get Request
     */
    refresh() {
        if (this.isExisting()) {
            return this._form.get(this.getRoute('show')).then((response) => {
                this.assignAttributes(response.data);
                return this;
            });
        }

        return Promise.resolve(this);
    }

    /**
     * Find request.
     *
     * @param identifier
     * @returns {PromiseLike<T> | Promise<T>}
     */
    find(identifier) {
        if (identifier === undefined) {
            throw new Error('You must specify the param on find() method.');
        }

        return this._form.get(this.getRoute('show', { identifier: identifier })).then((response) => {
            return new this.constructor(response.data);
        });
    }

    /**
     * First request
     *
     * @returns {PromiseLike<any> | Promise<any>}
     */
    first() {
        return this.get().then((response) => {
            let item;

            if (response.data) {
                item = response.data[0];
            } else {
                item = response[0];
            }

            return item || {};
        });
    }

    /**
     * Get request.
     *
     * @returns {PromiseLike<any> | Promise<any>}
     */
    get(params = null) {
        let route = this.getRoute('get', null, params);

        return this._form.get(route).then((response) => {
            return this.constructor.collect(response.data);
        });
    }

    /**
     * Create request.
     *
     * @param refresh
     */
    create(refresh = true) {
        return this._form.post(this.getRoute('create'), this._attributes).then((response) => {
            Bus.$emit(`modelEvent/${this.$resource}`, {
                event: 'create',
                resource: this.$resource,
            });

            if (refresh) {
                this.assignAttributes(_.get(response, 'data.data', response.data));
                return this;
            }

            return response;
        });
    }

    /**
     * Update model.
     *
     * @param params If object, the object will be send.  If undefined, the model attributes will be sent.  If array, only thos model attributes will be sent.
     * @param refresh
     * @returns {PromiseLike<any> | Promise<any>}
     */
    update(params, refresh = true) {
        let url = this.getRoute('update');

        if (_.isUndefined(params)) {
            params = this._attributes;
            if (this.isExisting() && this.getOption('patch')) {
                params = _.pick(params, this.changedAttributes(), this.getOption('identifier'));
            }
        } else if (Array.isArray(params)) {
            params = this.onlyAttributes(params);
        }

        return this._form.put(url, params).then((response) => {
            if (refresh) {
                this.assignAttributes(response.data);
            }

            Bus.$emit(`modelEvent/${this.$resource}`, {
                event: 'update',
                resource: this.$resource,
                id: this.id,
                _uid: this._uid,
            });

            return response;
        });
    }

    /**
     * Request.
     * @param config
     */
    request(config) {
        return this._form.sendForm(config.method, config.uri, config.data);
    }

    /**
     * Removes model or collection data from the database/API.
     *
     * @returns {Promise}
     */
    delete() {
        return this._form.delete(this.getRoute('delete')).then((response) => {
            Bus.$emit(`modelEvent/${this.$resource}`, {
                event: 'delete',
                resource: this.$resource,
                id: this.id,
                _uid: this._uid,
            });
            return response;
        });
    }

    /**
     * Save or update model.
     *
     * @param refresh
     * @returns {*}
     */
    save(refresh = true) {
        return this.isExisting() ? this.update(this, refresh) : this.create(refresh);
    }

    /**
     * Sync all instances of this model with same Id.
     */
    syncInstances(attributes) {
        if (this.isExisting()) {
            Bus.$emit(`modelEvent/${this.$resource}/${this.identifier()}/push`, attributes || this._attributes);
        }
    }

    /**
     * To Json.
     *
     * @returns {*}
     */
    toJSON() {
        return this._attributes;
    }

    /**
     * Registers a collection on this model. When this model is created it will
     * automatically be added to the collection. Similarly, when this model is
     * delete it will be remove from the collection. Registering the same
     * collection more than once has no effect.
     *
     * @param {Collection} collection
     */
    registerCollection(collection) {
        if (_.isArray(collection)) {
            _.each(collection, this.registerCollection);
            return;
        }

        if (_.isNil(collection) || _.isUndefined(collection._uid)) {
            throw new Error('Collection is not valid');
        }

        Vue.set(this._collections, collection._uid, collection);
    }

    /**
     * Removes a collection from this model's collection registry, removing all
     * effects that would occur when creating or deleting this model.
     *
     * Unregistering a collection that isn't registered has no effect.
     *
     * @param {Collection} collection
     */
    unregisterCollection(collection) {
        if (_.isArray(collection)) {
            _.each(collection, this.unregisterCollection);
            return;
        }

        if (_.isNil(collection) || _.isUndefined(collection._uid)) {
            throw new Error('Collection is not valid');
        }

        Vue.delete(this._collections, collection._uid);
    }

    /**
     * Adds this model to all registered collections.
     */
    addToAllCollections() {
        _.each(this._collections, (collection, id) => {
            collection.add(this);
        });
    }

    /**
     * Removes this model from all registered collections.
     */
    removeFromAllCollections() {
        _.each(this._collections, (collection, id) => {
            collection.remove(this);
        });
    }
}
